import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { Switch } from '../../components/SettingRow';
import { TwoColumnLayout, Column, Section, SectionBlock, SectionTitle } from '../../components/TwoColumnLayout';
import { ChoiceSwitch } from '../../components/ChoiceSwitch';
import { LineMeter } from '../../components/LineMeter';
import { ProductPackageTypeSelection } from './ProductPackageTypeSelection';
import { ServiceLimitProfileSelect } from '../../components/ServiceLimitProfileSelect';
import { SimCardInfo } from './SimCardInfo';
import { SubscriptionPrivacyForm } from '../../components/SubscriptionPrivacyForm';
import { RoamingLimitSelect } from '../../components/RoamingLimitSelect';
import { OfferingType } from '../../utils/offeringType';
import consumerProducts from '../../utils/consumerProducts';

interface Props {
	euData: number;
	productPackageType: any;
	simId: string;
	voiceMail: any;
	dataSuspended: any;
	internationalCallsAllowed: any;
	roamingLimit: any;
	dataRoamingAllowed: any;
	serviceLimitProfile: any;
	puk: string;
	settingsDisabled: boolean;
	suspendSubscriptionTemporarily: any;
	privacy: any;
	onChange: (value: any) => void;
	phoneDirectoryUserInfo: any;
	simClientInfoChangeHandler: (field: string, value: string) => void;
	showErrors: boolean;
	usageData: any;
	euDataRoamingLimit: any;
	settingsDisabledNotification: string;
	accountType: string;
	offeringType: OfferingType;
	customerContactData: any;
	subscriptionNumber: string;
	pollCurrentSim: () => void;
	settingChecked: (setting: any) => string;
}

const SettingsDisabledContainer = styled.div`
	border: 1px solid red;
	padding: 5px;
	background-color: hsl(0, 100%, 98%);
`;

const DisabledText = styled.div<{ settingsDisabled: boolean }>`
	color: ${(props) => (props.settingsDisabled ? 'lightgray' : 'inherit')};
`;

export const SimSettingsConsumer: React.FC<Props> = (props: Props) => {
	const [phoneDirectoryPrivacy, setPhoneDirectoryPrivacy] = useState<string>('');

	useEffect(() => {
		setPhoneDirectoryPrivacy(props.phoneDirectoryUserInfo.privacy);
	}, [props.phoneDirectoryUserInfo]);

	// TODO move this method to SubscriptionPrivacyForm?
	const handlePhoneDirectoryPrivacy = (value: string) => {
		if (phoneDirectoryPrivacy !== value) {
			props.onChange({ privacy: { name: 'privacy', value } });
			setPhoneDirectoryPrivacy(value);
		}
	};

	const settingsDisabled = props.settingsDisabled;

	let euDataText = null;
	if (props.euDataRoamingLimit) {
		euDataText = props.euDataRoamingLimit.value === 1 ? 'yksi gigatavu' : `${props.euDataRoamingLimit.value} gigatavua`;
	}

	return (
		<div>
			{settingsDisabled && (
				<SettingsDisabledContainer>{props.settingsDisabledNotification ? props.settingsDisabledNotification : 'Asetuksien muuttaminen ei ole tällä hetkellä mahdollista'}</SettingsDisabledContainer>
			)}
			<TwoColumnLayout>
				<DisabledText settingsDisabled={settingsDisabled}>
					<Column>
						{(props.productPackageType.disabled === undefined || !props.productPackageType.disabled) && (
							<div>
								<SectionTitle>Liittymätyyppi</SectionTitle>
								<Section>
									<SectionBlock>
										<ProductPackageTypeSelection
											productPackageType={props.productPackageType}
											offeringType={props.offeringType}
											customerContactData={props.customerContactData}
											subscriptionNumber={props.subscriptionNumber}
											pollCurrentSim={props.pollCurrentSim}
										/>
									</SectionBlock>
								</Section>
							</div>
						)}
						{!settingsDisabled && <SectionTitle>Asetukset</SectionTitle>}
						<Section>
							{(props.voiceMail.disabled === undefined || !props.voiceMail.disabled) && (
								<ChoiceSwitch
									setting={props.voiceMail}
									title={'Vastaaja (+' + consumerProducts.getSimVoiceMailPrice().toFixed(2) + ' €) '}
									variable={'voiceMail'}
									onChange={props.onChange}
									disabled={settingsDisabled || props.voiceMail.updating}
									checked={props.settingChecked(props.voiceMail)}
									updating={props.voiceMail.updating}
								/>
							)}
							{(props.dataSuspended.disabled === undefined || !props.dataSuspended.disabled) && (
								<ChoiceSwitch
									setting={props.dataSuspended}
									title={'Dataesto'}
									variable={'dataSuspended'}
									description={'Voit kytkeä netin tilapäisesti pois käytöstä ja takaisin päälle. Palvelu aktivoituu sekunneissa. Huomaathan, että tarvitset nettiä myös Mun Moin käyttöön.'}
									onChange={props.onChange}
									disabled={settingsDisabled || props.dataSuspended.updating}
									checked={props.settingChecked(props.dataSuspended)}
									updating={props.dataSuspended.updating}
								/>
							)}
						</Section>
						{(props.serviceLimitProfile.disabled === undefined || !props.serviceLimitProfile.disabled) && (
							<div>
								<ServiceLimitProfileSelect
									disabled={settingsDisabled || props.serviceLimitProfile.updating}
									onChange={(value) => props.onChange({ serviceLimitProfile: { name: 'serviceLimitProfile', value } })}
									accountType={props.accountType}
									value={props.settingChecked(props.serviceLimitProfile)}
									updating={props.serviceLimitProfile.updating}
								/>
							</div>
						)}
					</Column>
					<Column>
						<SectionTitle>Palvelut ulkomaille ja ulkomailla</SectionTitle>
						<Section>
							{props.usageData && !_.isNil(props.usageData.euData) && !_.isNil(props.euDataRoamingLimit) && (
								<SectionBlock>
									<LineMeter
										value={props.usageData.euData}
										maxvalue={parseFloat(props.euDataRoamingLimit.value)}
										color={'#93cb13'}
										additionalText={`${props.usageData.euData.toFixed(2)}Gt dataa käytetty EU-alueella`}
									/>
									<span>Simmin hintaan sisältyy {euDataText} dataa EU-alueella 30 päivän aikana. Ylimenevästä datasta veloitetaan 3,5 €/Gt + kotimaan hinta. </span>
								</SectionBlock>
							)}
							{(props.roamingLimit.disabled === undefined || !props.roamingLimit.disabled) && (
								<RoamingLimitSelect
									accountType={props.accountType}
									disabled={settingsDisabled || props.roamingLimit.updating}
									onChange={props.onChange}
									value={props.settingChecked(props.roamingLimit)}
									updating={props.roamingLimit.updating}
								/>
							)}
							{(props.dataRoamingAllowed.disabled === undefined || !props.dataRoamingAllowed.disabled) && (
								<ChoiceSwitch
									setting={props.dataRoamingAllowed}
									title={'Datan käyttö ulkomailla'}
									variable={'dataRoamingAllowed'}
									onChange={props.onChange}
									disabled={settingsDisabled || props.dataRoamingAllowed.updating}
									checked={props.settingChecked(props.dataRoamingAllowed)}
									updating={props.dataRoamingAllowed.updating}
								/>
							)}
							{(props.internationalCallsAllowed.disabled === undefined || !props.internationalCallsAllowed.disabled) && (
								<ChoiceSwitch
									setting={props.internationalCallsAllowed}
									title={'Puhelut sallittu Suomesta ulkomaille'}
									variable={'internationalCallsAllowed'}
									onChange={props.onChange}
									disabled={settingsDisabled || props.internationalCallsAllowed.updating}
									checked={props.settingChecked(props.internationalCallsAllowed)}
									updating={props.internationalCallsAllowed.updating}
								/>
							)}
						</Section>
						{(props.privacy.disabled === undefined || !props.privacy.disabled) && (
							<div>
								<SubscriptionPrivacyForm
									disabled={settingsDisabled}
									privacy={props.privacy}
									handlePhoneDirectoryPrivacy={handlePhoneDirectoryPrivacy}
									showErrors={props.showErrors}
									phoneDirectoryUserInfo={props.phoneDirectoryUserInfo}
									simClientInfoChangeHandler={props.simClientInfoChangeHandler}
									phoneDirectoryPrivacy={phoneDirectoryPrivacy}
									includeOwnerInfo={false}
									phoneDirectoryOwnerInfo={undefined}
									onChange={_.noop}
									checked={props.phoneDirectoryUserInfo.publishOwner}
								/>
							</div>
						)}
						<SimCardInfo simId={props.simId} puk={props.puk} disabled={settingsDisabled} />
						{(props.suspendSubscriptionTemporarily.disabled === undefined || !props.suspendSubscriptionTemporarily.disabled) && (
							<div>
								<SectionTitle>Hävisikö simmi?</SectionTitle>
								<Section>
									<Section>
										<SectionBlock>
											<Switch
												title="Sulje SIM-kortti väliaikaisesti"
												disabled={settingsDisabled || props.suspendSubscriptionTemporarily.updating}
												updating={props.suspendSubscriptionTemporarily.updating}
												infoText={'Asetusta päivitetään, tämä voi viedä muutaman minuutin'}
												checked={props.settingChecked(props.suspendSubscriptionTemporarily)}
												onChange={(checked: any) => props.onChange({ suspendSubscriptionTemporarily: { name: 'suspendSubscriptionTemporarily', value: checked } })}
											/>
										</SectionBlock>
									</Section>
								</Section>
							</div>
						)}
					</Column>
				</DisabledText>
			</TwoColumnLayout>
		</div>
	);
};
